import { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, Star, Eye, EyeSlash, X, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", rating: 5, text: "", date: "", source: "Google", visible: true, sort_order: 0 };
const inputCls = "w-full px-3 py-2.5 border border-[#E6E2D6] rounded-lg text-sm focus:outline-none focus:border-[#D4AF37] bg-white";

const Stars = ({ n, size = 14 }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={size} weight="fill" className={i <= n ? "text-[#D4AF37]" : "text-[#E6E2D6]"} />)}
  </span>
);

export const ReviewsManager = ({ apiUrl, authFetch }) => {
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${apiUrl}/api/admin/reviews`);
      const data = await res.json();
      setReviews(data.reviews || []);
    } catch {
      toast.error("Bewertungen konnten nicht geladen werden");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (form) => {
    const isNew = !form.id;
    const { id, created_at, ...payload } = form;
    payload.rating = Number(payload.rating);
    payload.sort_order = Number(payload.sort_order) || 0;
    const res = await authFetch(`${apiUrl}/api/admin/reviews${isNew ? "" : `/${id}`}`, {
      method: isNew ? "POST" : "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return toast.error(data.detail || "Speichern fehlgeschlagen");
    setReviews(isNew ? [...reviews, data] : reviews.map(r => (r.id === data.id ? data : r)));
    setEditing(null);
    toast.success(isNew ? "Bewertung hinzugefügt" : "Bewertung gespeichert");
  };

  const toggleVisible = (r) => save({ ...r, visible: !r.visible });

  const remove = async (r) => {
    if (!window.confirm(`Bewertung von „${r.name}" löschen?`)) return;
    const res = await authFetch(`${apiUrl}/api/admin/reviews/${r.id}`, { method: "DELETE" });
    if (res.ok) {
      setReviews(reviews.filter(x => x.id !== r.id));
      toast.success("Bewertung gelöscht");
    }
  };

  return (
    <div className="space-y-4" data-testid="reviews-manager">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-[#2C2C2C]/60 max-w-xl">
          Trage hier echte Google-Bewertungen ein (Vorname oder Initial, Sterne, Datum, Text). Sichtbare Bewertungen erscheinen auf der Startseite unter „Was unsere Kunden sagen".
        </p>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="review-create-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-white text-sm rounded-full hover:bg-[#2C2C2C] transition-colors whitespace-nowrap">
          <Plus size={16} weight="bold" /> Bewertung hinzufügen
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><SpinnerGap size={32} className="animate-spin text-[#D4AF37]" /></div>
      ) : reviews.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-[#E6E2D6] text-center">
          <Star size={40} className="mx-auto text-[#E6E2D6] mb-4" />
          <p className="text-[#2C2C2C]/60">Noch keine Bewertungen eingetragen</p>
        </div>
      ) : (
        reviews.map(r => (
          <div key={r.id} data-testid={`review-row-${r.id}`} className={`bg-white p-5 rounded-xl border ${r.visible ? "border-[#E6E2D6]" : "border-dashed border-[#E6E2D6] opacity-60"} flex flex-col sm:flex-row gap-4`}>
            <div className="flex-grow min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="font-medium">{r.name}</span>
                <Stars n={r.rating} />
                {r.date && <span className="text-xs text-[#2C2C2C]/50">{r.date}</span>}
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#F9F7F2] text-[#9A8C73]">{r.source}</span>
                {!r.visible && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">Ausgeblendet</span>}
              </div>
              <p className="text-sm text-[#2C2C2C]/80 italic">„{r.text}"</p>
            </div>
            <div className="flex sm:flex-col gap-1 flex-shrink-0">
              <button onClick={() => setEditing(r)} data-testid={`review-edit-${r.id}`} title="Bearbeiten" className="p-2 text-[#9A8C73] hover:text-[#2C2C2C] hover:bg-[#F9F7F2] rounded-lg"><PencilSimple size={18} /></button>
              <button onClick={() => toggleVisible(r)} data-testid={`review-toggle-${r.id}`} title={r.visible ? "Ausblenden" : "Einblenden"} className="p-2 text-[#9A8C73] hover:text-[#2C2C2C] hover:bg-[#F9F7F2] rounded-lg">{r.visible ? <EyeSlash size={18} /> : <Eye size={18} />}</button>
              <button onClick={() => remove(r)} data-testid={`review-delete-${r.id}`} title="Löschen" className="p-2 text-[#9A8C73] hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash size={18} /></button>
            </div>
          </div>
        ))
      )}

      {editing && <ReviewForm initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
};

const ReviewForm = ({ initial, onClose, onSave }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const onChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); onSave(form); }} data-testid="review-form"
        className="bg-white rounded-2xl w-full max-w-lg p-6 sm:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>{initial.id ? "Bewertung bearbeiten" : "Neue Bewertung"}</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[#F9F7F2] rounded-lg"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Name / Initial *</label>
            <input name="name" value={form.name} onChange={onChange} required className={inputCls} data-testid="review-name-input" placeholder="z.B. Sarah M." />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Datum</label>
            <input name="date" value={form.date || ""} onChange={onChange} className={inputCls} data-testid="review-date-input" placeholder="z.B. August 2026" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Sterne</label>
            <select name="rating" value={form.rating} onChange={onChange} className={inputCls} data-testid="review-rating-select">
              {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} Sterne</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Quelle</label>
            <input name="source" value={form.source} onChange={onChange} className={inputCls} data-testid="review-source-input" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Bewertungstext *</label>
            <textarea name="text" value={form.text} onChange={onChange} required rows={4} className={`${inputCls} resize-none`} data-testid="review-text-input" />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm rounded-full border border-[#E6E2D6] hover:bg-[#F9F7F2]">Abbrechen</button>
          <button type="submit" data-testid="review-save-btn" className="px-6 py-2.5 text-sm rounded-full bg-[#2C2C2C] text-white hover:bg-[#D4AF37] transition-colors">Speichern</button>
        </div>
      </form>
    </div>
  );
};
